import {Component, effect, ElementRef, inject, NgZone, OnInit, viewChild} from '@angular/core';
import {DatePipe, NgTemplateOutlet} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {forkJoin} from 'rxjs';
import {ContactRequirementHttpService} from './contact-requirement-http.service';
import {EmployeeHttpService} from '../../x-http-requests/employee-http.service';
import {EmploymentRelationshipHttpService} from '../../x-http-requests/employment-relationship-http.service';
import {
  ContactChannel,
  ContactGateVerdict,
  ContactRequirementGatingDraft,
  ContactRequirementOverviewEntry,
  ContactRequirementPolicy,
  ContactRequirementResource,
  ContactRequirementTargetType,
  contactGateVerdictLabel,
  contactRequirementPolicyLabel,
  defaultGatingDraft,
  existingStaffContactPolicyHint,
  GRACE_DAYS_DEFAULT,
  GRACE_DAYS_MAX,
  GRACE_DAYS_MIN,
  gatingDraftOf,
  isValidGraceDays,
  isValidRecheckIntervalDays,
  newStaffContactPolicyHint,
  RECHECK_INTERVAL_MAX,
  RECHECK_INTERVAL_MIN,
  recheckIntervalHint
} from './contact-requirement.models';
import {CONTACT_CHANNEL_OPTIONS, contactChannelLabel} from './contact-channels';
import {GlobalAlertService} from '../../../../_services/global-alert.service';
import {AlertLevel} from '../../../../enums/alert-level';
import {SelectComponent} from '../../../../_ui/components/controls/select/select.component';
import {CheckboxComponent} from '../../../../_ui/components/controls/checkbox/checkbox.component';
import {PinnedTableController} from '../../../../_ui/tables/pinned-table';

/**
 * One entry of the target picker. `name` is the whole option text — an employee's name or a relationship's — and
 * nothing else, exactly like the Forms requirements screen's own `Target`: it is what `<cqme-select>` searches
 * on, so the internal id never travels as visible text.
 */
interface Target {
  id: string;
  name: string;
}

/** One entry of a cohort policy dropdown. A frozen constant — see {@link ContactRequirementsComponent.policyOptions}. */
interface PolicyOption {
  label: string;
  value: ContactRequirementPolicy;
}

/** The one date format this screen renders, everywhere. See {@link ContactRequirementsComponent.formatDate}. */
const DATE_FORMAT = 'dd.MM.yyyy';

/**
 * Manager UI for required contact-channel linking (#558) — which employees / employment-relationships / the
 * whole company must have a channel (Telegram in v1) linked and shared with this employer. Reached from the
 * company navbar, beside the required-forms manager entry; backed by `/company/contact-requirement`
 * (ShardAdmin / CompanyOwner / PlatformOwner). See docs/articles/company/contact-channel-linking.md.
 *
 * Two families of screen meet here, and both are house patterns rather than anything invented locally:
 *
 * 1. **The cohort model** is the sibling of `_modules/forms/requirements/form-requirements.component.ts` — two
 *    independent backfill policies plus a shared grace window ({@link ContactRequirementPolicy}), a create card,
 *    and an in-place row editor. Two differences from that screen, both because the backend contract differs on
 *    purpose: a contact requirement can additionally target {@link ContactRequirementTargetType.AllEmployees}
 *    ("jeder, der hier arbeitet, muss X verknüpfen" is a real configuration here, and needs no target picker at
 *    all), and `ContactRequirementResource.targetName` is SERVER-resolved, so a row never cross-references the
 *    roster — the employee/relationship lists are loaded only to populate the create picker. It also adds a
 *    re-check interval (`recheckIntervalDays`) and a second section, the roster's link-state overview, neither
 *    of which the Forms screen has.
 * 2. **The two lists** are the employees roster's table (`../employees.component.ts`): the same `max-w-7xl`
 *    page container, and the same floating header — a presentational header clone plus a pinned horizontal
 *    scrollbar clone plus a frozen identity column, all driven by the SHARED {@link PinnedTableController}
 *    (`_ui/tables/pinned-table.ts`, extracted from that screen for this one) and styled by the shared
 *    `_ui/tables/pinned-table.css`. Below `md` both lists fall back to cards, per the style guide's
 *    "Data table + card fallback".
 *
 * The four things the first manual smoke (2026-09-09) reported, and where each is answered:
 *
 * - *"Make the whole UI wider"* — the page is `mx-auto max-w-7xl p-4` on a `block min-w-0` host, exactly the
 *   employees roster's container. It used to be `max-w-3xl` inside its own `absolute inset-0 overflow-y-auto`
 *   box, which was both narrower than the rest of the company area AND a second vertical scrollport nested in
 *   the shell's — the latter would have broken the floating header outright (a sticky box pins to the nearest
 *   scrollport, and `PINNED_HEAD_TOP_PX` is measured against the shell's).
 * - *"Date format should be dd.MM.yyyy"* — every date on this screen goes through {@link formatDate} or the
 *   `date: 'dd.MM.yyyy'` pipe. Never `toLocaleDateString`, which answered `9.9.2026`.
 * - *"The Nie checkbox jumps when the number input is shown"* — the re-check control is one `min-h-11` row, so
 *   the checkbox and the day count share a baseline and toggling cannot move anything below it.
 * - *"On small viewports there is an ugly overflow"* — `min-w-0` on the host and on every flex/grid child that
 *   holds free text, cards instead of a table below `md`, and the tables' own horizontal overflow contained by
 *   their `overflow-x-auto` wrapper (see the employees roster's note on why the host must be a block).
 */
@Component({
  selector: 'app-contact-requirements',
  standalone: true,
  // The host must be a block: as the default inline custom element (width 0) it does not contain the max-w-7xl
  // child, so the tables' overflow leaks up to the app-shell scroll container and the WHOLE PAGE scrolls
  // sideways at large-but-not-max widths. Same reasoning, and the same fix, as employees.component.ts (#140).
  host: {class: 'block min-w-0'},
  imports: [DatePipe, FormsModule, NgTemplateOutlet, SelectComponent, CheckboxComponent],
  // DatePipe is injected as well as imported: three strings that are built in TypeScript (the overview badges
  // and their tooltips) have to format a date too, and they must not diverge from the template's format.
  providers: [DatePipe],
  // The frozen column's edge, the header clone and the pinned scrollbar clone — the shared house pattern, in one
  // place, beside the controller that drives them. See docs/articles/frontend/style-guide.md.
  styleUrls: ['../../../../_ui/tables/pinned-table.css'],
  template: `
    <div class="mx-auto flex max-w-7xl flex-col gap-4 p-4" data-test="page-container">

      <div>
        <h1 class="text-2xl font-bold tracking-tight text-gray-900">Pflicht-Kontakte</h1>
        <p class="mt-1 max-w-prose text-sm text-gray-500">
          Legen Sie fest, für wen ein verknüpfter Kontaktkanal verpflichtend ist.
        </p>
      </div>

      @if (loading) {
        <div class="flex items-center justify-center py-20">
          <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
          </svg>
        </div>
      } @else if (error) {
        <!-- red-700, not red-600: see the Status badge / pill note in the style guide — red-600 fails AA here. -->
        <div class="rounded-2xl border-[1px] border-red-100 bg-red-50 p-4 shadow-sm">
          <p class="text-sm font-semibold text-red-700">Die Pflicht-Kontakte konnten nicht geladen werden.</p>
          <button (click)="load()" class="mt-2 inline-flex items-center rounded-md bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700 transition-all duration-200 hover:bg-red-200">Erneut versuchen</button>
        </div>
      } @else {

        <!-- Add -->
        <!-- The CONFIGURATION card keeps the field order, grouping and controls it had before the #558 list
             rework, on the user's explicit instruction after the second smoke — one field per row, the target
             strip directly under the channel, the two cohort policies side by side, then Fristlänge and Erneute
             Prüfung — the last two now 2-up on the SAME grid as the cohort pair, since the wider card left the
             right half of those two rows empty. The only things carried over from the rework are the container width (the page is now the
             company area's max-w-7xl, so this card is wider than it was) and the three fixes the first smoke
             asked for: the wrapping target strip, the fixed-height re-check row, and dd.MM.yyyy. -->
        <div class="space-y-3 rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
          <p class="text-base font-bold tracking-tight text-gray-800">Neue Zuweisung</p>

          <div class="space-y-3">
            <div>
              <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Kanal</label>
              <cqme-select data-testid="channel-select" class="block w-full"
                           [options]="channelOptions" [labelKey]="'label'" [valueKey]="'value'"
                           size="md" [clearable]="false" ariaLabel="Kanal"
                           [current]="channel" (currentChange)="setChannel($event)"></cqme-select>
            </div>

            <!-- target kind: Alle Mitarbeiter | Arbeitsverhältnis | Mitarbeiter.
                 grow + shrink-0 + basis-auto on the buttons is load-bearing and is NOT interchangeable with
                 flex-1. flex-1 means "flex: 1 1 0%", so the buttons shrink below their own text instead of
                 wrapping, and whitespace-nowrap then paints the three labels ON TOP of one another (measured at
                 390px — the visible half of the reported "ugly overflow"). "flex: 1 0 auto" sizes each button to
                 its label, refuses to shrink, and lets flex-wrap take the third onto a second line, where the
                 two on the first line grow to fill it.
                 NOTE: no backticks in this comment — the template is a TS template literal and one would
                 terminate it mid-markup (it did, on the first pass here). -->
            <!-- sm:inline-flex, NOT sm:w-auto on a block flex box: width:auto on a block-level flex container
                 still fills its parent, so the strip painted a 1180px frame with 900px of empty space beside
                 three buttons. inline-flex shrink-wraps to its content, which is what it did before the rework. -->
            <div role="group" aria-label="Zielgruppe"
                 class="flex w-full flex-wrap gap-1 rounded-xl border-[1px] border-gray-200 bg-white p-1 shadow-sm sm:inline-flex sm:w-auto">
              @for (kind of targetKinds; track kind.value) {
                <button type="button" (click)="setTargetType(kind.value)" [attr.data-test]="kind.test"
                        [attr.aria-pressed]="targetType === kind.value"
                        class="shrink-0 grow basis-auto whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-200 sm:grow-0"
                        [class.bg-theme-primary-50]="targetType === kind.value"
                        [class.text-theme-primary-700]="targetType === kind.value"
                        [class.shadow-sm]="targetType === kind.value"
                        [class.text-gray-500]="targetType !== kind.value">{{ kind.label }}</button>
              }
            </div>

            @if (targetType !== TargetType.AllEmployees) {
              <div>
                <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
                  {{ targetType === TargetType.Employee ? 'Mitarbeiter' : 'Arbeitsverhältnis' }}
                </label>
                <!-- The shared cqme-select, never a native <select> — see form-requirements.component.ts's own
                     note on the same control for why (browser type-ahead + no theme tokens on a native list). -->
                <cqme-select #targetPicker data-testid="target-select" class="block w-full"
                             [options]="availableTargets" [labelKey]="'name'" [valueKey]="'id'"
                             size="md" [clearable]="true" placeholder="Bitte wählen…"
                             [ariaLabel]="targetType === TargetType.Employee ? 'Mitarbeiter' : 'Arbeitsverhältnis'"
                             [current]="selectedTargetId"
                             (currentChange)="setSelectedTarget($event)"></cqme-select>
                @if (availableTargets.length === 0) {
                  <p class="mt-2 text-xs text-gray-600">Alle {{ targetType === TargetType.Employee ? 'Mitarbeiter' : 'Arbeitsverhältnisse' }} sind für diesen Kanal bereits zugewiesen.</p>
                }
              </div>
            } @else {
              <p class="text-xs text-gray-600" data-test="all-employees-hint">
                Gilt für jeden aktuellen und künftigen Mitarbeiter des Unternehmens.
              </p>
            }

            <ng-container [ngTemplateOutlet]="gatingFields"
                          [ngTemplateOutletContext]="{$implicit: newAssignment, prefix: 'create'}"></ng-container>

            <div class="flex justify-end">
              <button type="button" (click)="add()" [disabled]="!canSubmitNew" data-test="assign"
                      class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5 py-2.5 font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl active:scale-95 disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale">
                Zuweisen
              </button>
            </div>
          </div>
        </div>

        <!-- Current requirements -->
        <div>
          <div class="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <p class="text-base font-bold tracking-tight text-gray-800" id="requirements-heading">Aktuelle Zuweisungen</p>
            @if (requirements.length > 0) {
              <p class="text-sm text-gray-600" data-test="requirements-count">
                <span class="font-semibold text-gray-700">{{ requirements.length }}</span>
                {{ requirements.length === 1 ? 'Zuweisung' : 'Zuweisungen' }}
              </p>
            }
          </div>

          @if (requirements.length === 0) {
            <p class="rounded-2xl border-2 border-dashed border-gray-100 py-8 text-center text-sm italic text-gray-500">
              Aktuell ist für niemanden ein Kontaktkanal verpflichtend.
            </p>
          } @else {

            <!-- md and up: the employees roster's table, floating header and all. The cohort names live in the
                 column headings so a row states only its values; the identity column is FROZEN so scrolling
                 across to the deadlines never loses whose row you are reading. -->
            <div class="relative hidden md:block">

              <!-- THE PINNED COLUMN HEADERS. Purely presentational (aria-hidden, nothing tabbable inside), a
                   CLONE because the real <thead> cannot stick: overflow-x on the wrapper makes it a scrollport
                   on both axes, so a sticky cell inside it pins to the WRAPPER and scrolls away with the page.
                   The full reasoning, and why an IntersectionObserver is not used for the hand-over, is on
                   PinnedTableController. Tell the two apart by data-test: the clone is
                   [data-test="requirements-header-clone"] and is NOT inside [data-test="requirements-table"]. -->
              <div class="cq-pinned-head sticky z-30 h-0" [class.cq-pinned-head-on]="requirementsTable.headerPinned()"
                   aria-hidden="true" data-test="requirements-header-clone">
                <div class="overflow-hidden rounded-2xl border-[1px] border-gray-100 bg-gray-50 shadow-sm" #reqHeadViewport>
                  <table class="text-sm" [style.width.px]="requirementsTable.pinnedHeadWidth()">
                    <colgroup>
                      @for (width of requirementsTable.columnWidths(); track $index) {
                        <col [style.width.px]="width">
                      }
                    </colgroup>
                    <thead>
                      <tr class="whitespace-nowrap text-left text-xs uppercase tracking-wide text-gray-500">
                        <th class="cq-frozen-edge sticky left-0 z-20 bg-gray-50 px-4 py-3 font-semibold"
                            [class.cq-frozen-edge-on]="requirementsTable.scrolledX()">Ziel</th>
                        <th class="px-4 py-3 font-semibold">Gültig ab</th>
                        <th class="px-4 py-3 font-semibold">Bestehende Belegschaft</th>
                        <th class="px-4 py-3 font-semibold">Neu &amp; Wiedereintritt</th>
                        <th class="px-4 py-3 font-semibold">Erneute Prüfung</th>
                        <th class="px-4 py-3 text-right font-semibold">Aktionen</th>
                      </tr>
                    </thead>
                  </table>
                </div>
              </div>

              <!-- tabindex/role/aria-label: a region that really scrolls must be reachable by KEYBOARD alone
                   (axe "scrollable-region-focusable"), which matters more here than usual because the wrapper's
                   own scrollbar is suppressed in favour of the pinned clone below. -->
              <div class="cq-table-wrapper overflow-x-auto rounded-2xl border-[1px] border-gray-100 bg-white shadow-sm"
                   #reqWrapper data-test="requirements-table" tabindex="0" role="group"
                   aria-label="Aktuelle Zuweisungen">
                <table class="min-w-full divide-y divide-gray-100 text-sm" aria-labelledby="requirements-heading" #reqTable>
                  <thead class="bg-gray-50" #reqHead>
                    <tr class="whitespace-nowrap text-left text-xs uppercase tracking-wide text-gray-500" #reqHeadRow>
                      <!-- cq-frozen-edge only PAINTS once the wrapper is actually scrolled sideways: at rest
                           there is nothing behind the column, so an edge there is just a stray line. -->
                      <th scope="col" class="cq-frozen-edge sticky left-0 z-20 bg-gray-50 px-4 py-3 font-semibold"
                          [class.cq-frozen-edge-on]="requirementsTable.scrolledX()">Ziel</th>
                      <th scope="col" class="px-4 py-3 font-semibold">Gültig ab</th>
                      <th scope="col" class="px-4 py-3 font-semibold">Bestehende Belegschaft</th>
                      <th scope="col" class="px-4 py-3 font-semibold">Neu &amp; Wiedereintritt</th>
                      <th scope="col" class="px-4 py-3 font-semibold">Erneute Prüfung</th>
                      <th scope="col" class="px-4 py-3 text-right font-semibold">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    @for (r of requirements; track r.id) {
                      <!-- group: the frozen cell carries its own opaque background (a sticky cell is lifted out
                           of the row's paint order and no longer inherits it), so the row hover has to be
                           re-applied there or it visibly stops at the freeze boundary. Both sides need the !
                           modifier because Bootstrap 5 ships .bg-white{…!important} — see the style guide. -->
                      <tr class="group transition-colors hover:bg-gray-50">
                        <th scope="row"
                            class="cq-frozen-edge sticky left-0 z-10 !bg-white px-4 py-3 text-left align-top font-normal transition-colors group-hover:!bg-gray-50"
                            [class.cq-frozen-edge-on]="requirementsTable.scrolledX()">
                          <div class="flex min-h-8 flex-col justify-center">
                            <!-- truncate + title rather than break-words: under the auto table layout a long
                                 name would otherwise widen the FROZEN column past the viewport, which is the
                                 one column that must always fit. -->
                            <span class="block max-w-[16rem] truncate font-semibold text-gray-900"
                                  [title]="targetName(r)" data-test="target-name">{{ targetName(r) }}</span>
                            <span class="mt-0.5 block text-xs text-gray-500"
                                  data-test="target-type">{{ targetSubline(r) }}</span>
                          </div>
                        </th>
                        <!-- Every value cell is min-h-8 items-START, not items-center, and that is an alignment
                             fix rather than a preference: the identity cell is TWO lines (name + kind · channel),
                             so a value centred in its own box lands on the MIDDLE of that block and reads 6px
                             below the name it belongs to — measured, and visible as a staggered row. Top-aligned,
                             the value's line box and the name's line box share a top edge and, being the same
                             height, a centre. min-h-8 stays so a row keeps its height when a cell is empty. -->
                        <td class="whitespace-nowrap px-4 py-3 align-top text-gray-700" data-test="effective-from">
                          <div class="flex min-h-8 items-start">{{ r.effectiveFrom | date: DateFormat }}</div>
                        </td>
                        <td class="px-4 py-3 align-top" data-test="existing-staff-value">
                          <div class="flex min-h-8 items-start">
                            <ng-container [ngTemplateOutlet]="existingStaffValue" [ngTemplateOutletContext]="{$implicit: r}"></ng-container>
                          </div>
                        </td>
                        <td class="px-4 py-3 align-top" data-test="new-staff-value">
                          <div class="flex min-h-8 items-start">
                            <ng-container [ngTemplateOutlet]="newStaffValue" [ngTemplateOutletContext]="{$implicit: r}"></ng-container>
                          </div>
                        </td>
                        <td class="whitespace-nowrap px-4 py-3 align-top" data-test="recheck-value">
                          <div class="flex min-h-8 items-start text-gray-700">{{ recheckLabel(r) }}</div>
                        </td>
                        <td class="px-4 py-3 align-top">
                          <!-- -mt-1.5: a row action is a 32px button around a 20px glyph, so top-aligned with
                               the other values its ICON centres 6px below the name's line. Pulling the box up by
                               exactly that difference puts the glyph on the name's centre line, and leaves the
                               cell's own height (min-h-8) alone. -->
                          <div class="-mt-1.5 flex min-h-8 items-start justify-end gap-1" data-test="row-actions">
                            <ng-container [ngTemplateOutlet]="rowActions" [ngTemplateOutletContext]="{$implicit: r}"></ng-container>
                          </div>
                        </td>
                      </tr>
                      @if (editingId === r.id && editDraft) {
                        <tr>
                          <td colspan="6" class="px-4 pb-4">
                            <!-- sticky left-0: the editor is a form, not table data, so it stays at the wrapper's
                                 left edge instead of sliding out of reach when the table is scrolled across. -->
                            <div class="sticky left-0 max-w-4xl">
                              <ng-container [ngTemplateOutlet]="rowEditor" [ngTemplateOutletContext]="{$implicit: r, prefix: 'edit'}"></ng-container>
                            </div>
                          </td>
                        </tr>
                      }
                    }
                  </tbody>
                </table>
              </div>

              <!-- THE PINNED HORIZONTAL SCROLLBAR — the deliberate mirror of the header clone, outside the
                   wrapper for the same measured reason, and the ONLY horizontal control for this table (the
                   wrapper's own bar is suppressed by .cq-table-wrapper). Purely presentational: aria-hidden and
                   nothing tabbable, while the wrapper keeps the keyboard-reachable role. -->
              <div class="cq-pinned-scroll sticky z-30 h-0"
                   [class.cq-pinned-scroll-on]="requirementsTable.scrollbarPinned()"
                   aria-hidden="true" data-test="requirements-scrollbar-clone">
                <div class="cq-pinned-scroll-viewport absolute inset-x-0 bottom-0 overflow-x-auto border-t border-gray-200 bg-gray-50"
                     #reqScrollViewport>
                  <!-- The 1px spacer IS the scrollable content; its width is the same measured table width the
                       header clone uses, so both clones and the wrapper share one scroll range. -->
                  <div class="h-px" [style.width.px]="requirementsTable.pinnedHeadWidth()"></div>
                </div>
              </div>
            </div>

            <!-- Below md: one card per requirement, restating the labels as a description list. -->
            <ul class="grid grid-cols-1 gap-3 md:hidden" data-test="requirements-cards">
              @for (r of requirements; track r.id) {
                <li class="rounded-2xl border-[1px] border-gray-100 bg-white p-3 shadow-sm">
                  <div class="flex items-start gap-3">
                    <div class="min-w-0 flex-1">
                      <p class="break-words text-sm font-semibold text-gray-900" data-test="target-name">{{ targetName(r) }}</p>
                      <p class="mt-0.5 break-words text-xs text-gray-500" data-test="target-type">{{ targetSubline(r) }}</p>
                    </div>
                    <!-- -mt-1.5 for the same reason as the table's action cell: the glyph centres on the card's
                         title line rather than 6px under it. -->
                    <div class="-mt-1.5 flex shrink-0 items-start gap-1" data-test="row-actions">
                      <ng-container [ngTemplateOutlet]="rowActions" [ngTemplateOutletContext]="{$implicit: r}"></ng-container>
                    </div>
                  </div>

                  <dl class="mt-3 space-y-2 border-t border-gray-100 pt-3">
                    <div>
                      <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Gültig ab</dt>
                      <dd class="mt-0.5 text-sm text-gray-700" data-test="effective-from">{{ r.effectiveFrom | date: DateFormat }}</dd>
                    </div>
                    <div>
                      <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Bestehende Belegschaft</dt>
                      <dd class="mt-0.5 text-sm" data-test="existing-staff-value">
                        <ng-container [ngTemplateOutlet]="existingStaffValue" [ngTemplateOutletContext]="{$implicit: r}"></ng-container>
                      </dd>
                    </div>
                    <div>
                      <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Neu &amp; Wiedereintritt</dt>
                      <dd class="mt-0.5 text-sm" data-test="new-staff-value">
                        <ng-container [ngTemplateOutlet]="newStaffValue" [ngTemplateOutletContext]="{$implicit: r}"></ng-container>
                      </dd>
                    </div>
                    <div>
                      <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Erneute Prüfung</dt>
                      <dd class="mt-0.5 text-sm text-gray-700" data-test="recheck-value">{{ recheckLabel(r) }}</dd>
                    </div>
                  </dl>

                  @if (editingId === r.id && editDraft) {
                    <div class="mt-3 border-t border-gray-100 pt-3">
                      <ng-container [ngTemplateOutlet]="rowEditor" [ngTemplateOutletContext]="{$implicit: r, prefix: 'edit-card'}"></ng-container>
                    </div>
                  }
                </li>
              }
            </ul>
          }
        </div>

        <!-- Roster overview: per-employee link state. Collapsed by default and loaded on first expand, so a
             company with many employees does not pay for it on every visit to this screen. -->
        <div>
          <!-- THE DISCLOSURE HEADER. A native button spanning the whole header row, for three separate reasons,
               each of which was a real complaint:

               (a) It is a real <button type="button">, so the platform gives it Enter/Space activation, a tab
                   stop and a role for free — nothing here re-implements keyboard handling.
               (b) It carries a VERB, not a lone chevron. As a card-styled bar with a bare chevron 1200px from
                   the title it did not read as clickable at all ("does not read as clickable"); the chevron now
                   sits beside "Anzeigen"/"Ausblenden" in a chip that lights up with the row, which is what makes
                   the affordance legible at a glance.
               (c) It carries no card chrome of its own, so its title starts on the page container's left edge —
                   level with the sibling heading "Aktuelle Zuweisungen", which as a bordered box it was 24px
                   inside of. -mx-2 px-2 lets the hover tint bleed past the text without moving it.

               aria-controls names a panel that is ALWAYS in the DOM (the @if lives inside it), because an
               aria-controls pointing at an id that does not exist is an axe "aria-valid-attr-value" violation
               exactly while the section is collapsed — i.e. in its default state. -->
          <button type="button" (click)="toggleOverview()" data-test="toggle-overview"
                  [attr.aria-expanded]="overviewOpen" aria-controls="overview-panel"
                  class="group -mx-2 mb-2 flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl px-2 py-1.5 text-left transition-colors duration-200 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-primary-500">
            <span class="text-base font-bold tracking-tight text-gray-800">Verknüpfungsstatus</span>
            <span class="inline-flex shrink-0 items-center gap-1.5 rounded-lg border-[1px] border-gray-200 bg-white px-2.5 py-1 text-sm font-medium text-gray-600 shadow-sm transition-colors duration-200 group-hover:border-gray-300 group-hover:bg-gray-100 group-hover:text-gray-900">
              <span data-test="toggle-overview-label">{{ overviewOpen ? 'Ausblenden' : 'Anzeigen' }}</span>
              <svg class="h-4 w-4 transition-transform duration-200" [class.rotate-180]="overviewOpen"
                   fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
            </span>
          </button>

          <!-- ALWAYS rendered, empty while collapsed: it is the target of the header's aria-controls, and a
               reference to a missing id is an axe violation (see the header's note). No mt-*: the disclosure row
               above carries the same mb-2 the assignments heading does, so both sections have the identical
               heading-to-content gap. -->
          <div id="overview-panel">
          @if (overviewOpen) {
            <div>
              @if (overviewLoading) {
                <div class="flex items-center justify-center py-10">
                  <svg class="h-6 w-6 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
                  </svg>
                </div>
              } @else if (overviewError) {
                <div class="rounded-2xl border-[1px] border-red-100 bg-red-50 p-4 shadow-sm">
                  <p class="text-sm font-semibold text-red-700">Der Verknüpfungsstatus konnte nicht geladen werden.</p>
                  <button (click)="loadOverview()" class="mt-2 inline-flex items-center rounded-md bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700 transition-all duration-200 hover:bg-red-200">Erneut versuchen</button>
                </div>
              } @else if (overview.length === 0) {
                <p class="rounded-2xl border-2 border-dashed border-gray-100 py-8 text-center text-sm italic text-gray-500">
                  Keine Mitarbeiter mit einer Kontakt-Pflicht.
                </p>
              } @else {
                <!-- The same floating-header table as the assignments list above, with its own controller: the
                     two lists scroll independently and each clone is clamped to its own table's box, so only
                     the one you are actually over is ever at the pin line. -->
                <div class="relative hidden md:block">
                  <div class="cq-pinned-head sticky z-30 h-0" [class.cq-pinned-head-on]="overviewTable.headerPinned()"
                       aria-hidden="true" data-test="overview-header-clone">
                    <div class="overflow-hidden rounded-2xl border-[1px] border-gray-100 bg-gray-50 shadow-sm" #ovHeadViewport>
                      <table class="text-sm" [style.width.px]="overviewTable.pinnedHeadWidth()">
                        <colgroup>
                          @for (width of overviewTable.columnWidths(); track $index) {
                            <col [style.width.px]="width">
                          }
                        </colgroup>
                        <thead>
                          <tr class="whitespace-nowrap text-left text-xs uppercase tracking-wide text-gray-500">
                            <th class="cq-frozen-edge sticky left-0 z-20 bg-gray-50 px-4 py-3 font-semibold"
                                [class.cq-frozen-edge-on]="overviewTable.scrolledX()">Mitarbeiter</th>
                            <th class="px-4 py-3 font-semibold">Status</th>
                            <th class="px-4 py-3 font-semibold">Kanäle</th>
                          </tr>
                        </thead>
                      </table>
                    </div>
                  </div>

                  <div class="cq-table-wrapper overflow-x-auto rounded-2xl border-[1px] border-gray-100 bg-white shadow-sm"
                       #ovWrapper data-test="overview-table" tabindex="0" role="group" aria-label="Verknüpfungsstatus">
                    <table class="min-w-full divide-y divide-gray-100 text-sm" aria-label="Verknüpfungsstatus" #ovTable>
                      <thead class="bg-gray-50" #ovHead>
                        <tr class="whitespace-nowrap text-left text-xs uppercase tracking-wide text-gray-500" #ovHeadRow>
                          <th scope="col" class="cq-frozen-edge sticky left-0 z-20 bg-gray-50 px-4 py-3 font-semibold"
                              [class.cq-frozen-edge-on]="overviewTable.scrolledX()">Mitarbeiter</th>
                          <th scope="col" class="px-4 py-3 font-semibold">Status</th>
                          <th scope="col" class="px-4 py-3 font-semibold">Kanäle</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-100">
                        @for (o of overview; track o.employeeId) {
                          <tr class="group transition-colors hover:bg-gray-50">
                            <th scope="row"
                                class="cq-frozen-edge sticky left-0 z-10 !bg-white px-4 py-3 text-left align-top font-normal transition-colors group-hover:!bg-gray-50"
                                [class.cq-frozen-edge-on]="overviewTable.scrolledX()">
                              <span class="block max-w-[16rem] truncate font-semibold text-gray-900"
                                    [title]="o.name" data-test="overview-name">{{ o.name }}</span>
                            </th>
                            <td class="px-4 py-3 align-top" data-test="overview-verdict">
                              <div class="flex min-h-8 items-start">
                                <ng-container [ngTemplateOutlet]="verdictBadge" [ngTemplateOutletContext]="{$implicit: o}"></ng-container>
                              </div>
                            </td>
                            <td class="px-4 py-3 align-top" data-test="overview-channels">
                              <div class="flex min-h-8 flex-wrap items-start gap-1">
                                <ng-container [ngTemplateOutlet]="channelBadges" [ngTemplateOutletContext]="{$implicit: o}"></ng-container>
                              </div>
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>

                  <div class="cq-pinned-scroll sticky z-30 h-0"
                       [class.cq-pinned-scroll-on]="overviewTable.scrollbarPinned()"
                       aria-hidden="true" data-test="overview-scrollbar-clone">
                    <div class="cq-pinned-scroll-viewport absolute inset-x-0 bottom-0 overflow-x-auto border-t border-gray-200 bg-gray-50"
                         #ovScrollViewport>
                      <div class="h-px" [style.width.px]="overviewTable.pinnedHeadWidth()"></div>
                    </div>
                  </div>
                </div>

                <ul class="grid grid-cols-1 gap-3 md:hidden" data-test="overview-cards">
                  @for (o of overview; track o.employeeId) {
                    <li class="rounded-2xl border-[1px] border-gray-100 bg-white p-3 shadow-sm">
                      <div class="flex items-start justify-between gap-3">
                        <span class="min-w-0 break-words text-sm font-semibold text-gray-900" data-test="overview-name">{{ o.name }}</span>
                        <ng-container [ngTemplateOutlet]="verdictBadge" [ngTemplateOutletContext]="{$implicit: o}"></ng-container>
                      </div>
                      <div class="mt-2 flex flex-wrap items-center gap-1" data-test="overview-channels">
                        <ng-container [ngTemplateOutlet]="channelBadges" [ngTemplateOutletContext]="{$implicit: o}"></ng-container>
                      </div>
                    </li>
                  }
                </ul>
              }
            </div>
          }
          </div>
        </div>
      }
    </div>

    <!-- One requirement's two cohort values, defined once and rendered by both layouts (see the equivalent
         template on form-requirements.component.ts for the full rationale). -->
    <ng-template #existingStaffValue let-r>
      @if (r.existingStaffPolicy === Policy.Grace) {
        <span data-test="policy-badge" data-policy="window" [title]="existingHint(r)"
              class="inline-flex items-center whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 align-top text-xs font-semibold text-amber-700">
          @if (r.existingStaffDeadline) {
            Frist bis {{ r.existingStaffDeadline | date: DateFormat }}
          } @else {
            Frist von {{ r.graceDays }} Tagen
          }
        </span>
      } @else {
        <span data-test="policy-badge" data-policy="locked" [title]="existingHint(r)"
              class="inline-flex items-center whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 align-top text-xs font-semibold text-red-700">Sofort gesperrt</span>
      }
    </ng-template>

    <ng-template #newStaffValue let-r>
      @if (r.newStaffPolicy === Policy.Grace) {
        <span data-test="policy-badge" data-policy="window" [title]="newHint(r)"
              class="inline-flex items-center whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 align-top text-xs font-semibold text-amber-700">{{ r.graceDays }} Tage ab Eintritt</span>
      } @else {
        <span data-test="policy-badge" data-policy="locked" [title]="newHint(r)"
              class="inline-flex items-center whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 align-top text-xs font-semibold text-red-700">Sofort gesperrt</span>
      }
    </ng-template>

    <ng-template #rowActions let-r>
      <button type="button" (click)="toggleEdit(r)" data-test="edit-requirement"
              [attr.aria-expanded]="editingId === r.id"
              [attr.aria-label]="'Zuweisung für ' + targetName(r) + ' bearbeiten'"
              title="Bearbeiten"
              class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 20h4l10-10a2.83 2.83 0 10-4-4L4 16v4z" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6"/>
        </svg>
      </button>
      <button type="button" (click)="remove(r)"
              [attr.aria-label]="'Zuweisung für ' + targetName(r) + ' entfernen'" title="Entfernen"
              class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-7 0v12a1 1 0 001 1h6a1 1 0 001-1V7" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6"/>
        </svg>
      </button>
    </ng-template>

    <ng-template #rowEditor let-r let-prefix="prefix">
      <div class="space-y-3" data-test="requirement-editor">
        <ng-container [ngTemplateOutlet]="gatingFields" [ngTemplateOutletContext]="{$implicit: editDraft, prefix: prefix}"></ng-container>
        <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" (click)="cancelEdit()"
                  class="inline-flex items-center justify-center rounded-2xl border-[1px] border-gray-200 px-4 py-2.5 font-semibold text-gray-600 shadow-sm transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 active:scale-95">
            Abbrechen
          </button>
          <button type="button" (click)="saveEdit(r)" [disabled]="!canSaveEdit" data-test="save-requirement"
                  class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5 py-2.5 font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl active:scale-95 disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale">
            Speichern
          </button>
        </div>
      </div>
    </ng-template>

    <!-- The gating settings, rendered three times (new assignment / the table's row editor / the card
         fallback's row editor) — "prefix" keeps every id unique between the copies. -->
    <ng-template #gatingFields let-draft let-prefix="prefix">
      <div class="space-y-3">
        <p class="text-sm font-bold tracking-tight text-gray-700">Bei fehlender Verknüpfung</p>
        <p class="max-w-prose text-xs text-gray-600">
          Stichtag ist der Beginn dieser Zuweisung. Zur bestehenden Belegschaft zählt, wer seit davor ohne
          Unterbrechung beschäftigt ist. Wer danach eintritt oder nach einer Unterbrechung zurückkehrt, gilt als
          neu.
        </p>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div class="min-w-0">
            <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Bestehende Belegschaft</label>
            <cqme-select class="block w-full" [attr.data-testid]="prefix + '-existing-policy'"
                         [options]="policyOptions" [labelKey]="'label'" [valueKey]="'value'"
                         size="md" ariaLabel="Bestehende Belegschaft"
                         [current]="draft.existingStaffPolicy"
                         (currentChange)="setExistingPolicy(draft, $event)"></cqme-select>
            <p class="mt-2 text-xs text-gray-600" [attr.data-test]="prefix + '-existing-hint'">{{ existingHintFor(draft) }}</p>
          </div>

          <div class="min-w-0">
            <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Neu eingetretene und zurückkehrende Mitarbeiter</label>
            <cqme-select class="block w-full" [attr.data-testid]="prefix + '-new-policy'"
                         [options]="policyOptions" [labelKey]="'label'" [valueKey]="'value'"
                         size="md" ariaLabel="Neu eingetretene und zurückkehrende Mitarbeiter"
                         [current]="draft.newStaffPolicy"
                         (currentChange)="setNewPolicy(draft, $event)"></cqme-select>
            <p class="mt-2 text-xs text-gray-600" [attr.data-test]="prefix + '-new-hint'">{{ newHintFor(draft) }}</p>
          </div>
        </div>

        <!-- Fristlänge and Erneute Prüfung share ONE 2-up row, on the SAME grid as the two cohort selects above
             (grid-cols-1 → sm:grid-cols-2, gap-3) rather than a second, differently-breaking one: with the card
             now at the company area's max-w-7xl, two full-width rows of a 112px number input left the entire
             right half of the card empty, and a grid that broke at a different width than the cohort row above
             it would paint the card 2-up in one place and 1-up in the other between sm and md.
             min-w-0 on both columns for the same reason it is on the cohort columns — a grid item's default
             min-width:auto refuses to shrink below its content, and the helper texts here are long enough to
             push the column wider than its track. -->
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2" [attr.data-test]="prefix + '-timing-row'">
          <div class="min-w-0">
            <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" [attr.for]="prefix + '-grace-days'">Fristlänge</label>
            <!-- min-h-12 matches the re-check row beside it, so the two value rows sit on ONE baseline whatever the
                 re-check row is currently showing. TWELVE, not eleven: the input is 42px at sm+ but 46px below it
                 (the house inputs are sm:text-sm, i.e. 16px on a phone so iOS does not zoom on focus), so a 44px
                 floor was under the control at phone width and the re-check row still grew by 2px when the day
                 count appeared. 48px clears both. -->
            <div class="flex min-h-12 flex-wrap items-center gap-x-2 gap-y-1">
              <input type="number" inputmode="numeric" step="1" [attr.min]="GraceMin" [attr.max]="GraceMax"
                     [id]="prefix + '-grace-days'" [attr.data-test]="prefix + '-grace-days'"
                     [ngModel]="draft.graceDays" (ngModelChange)="draft.graceDays = $event"
                     [attr.aria-invalid]="graceDaysInvalid(draft)"
                     [attr.aria-describedby]="prefix + '-grace-days-hint'"
                     class="block w-28 rounded-xl border-[1px] bg-white px-3 py-2.5 shadow-sm transition-all duration-200 focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"
                     [class.border-red-300]="graceDaysInvalid(draft)"
                     [class.border-gray-200]="!graceDaysInvalid(draft)"/>
              <span class="text-sm text-gray-600">Tage</span>
            </div>
            <p class="mt-2 max-w-prose text-xs text-gray-600" [id]="prefix + '-grace-days-hint'">
              Gilt für jede hier gewährte Frist und für eine überfällige erneute Prüfung.
            </p>
            @if (graceDaysInvalid(draft)) {
              <p class="mt-2 text-xs font-semibold text-red-600"
                 [attr.data-test]="prefix + '-grace-days-error'" role="alert">
                Bitte eine ganze Zahl zwischen {{ GraceMin }} und {{ GraceMax }} eintragen.
              </p>
            }
          </div>

          <div class="min-w-0">
            <!-- A <p> rather than a <label for>: the control this names is the CHECKBOX plus an input that is
                 only there half the time, and a label pointing at an absent id names nothing. It renders exactly
                 as the label did, so the field reads unchanged. -->
            <p class="mb-2 text-sm font-bold tracking-tight text-gray-700" [id]="prefix + '-recheck-label'">Erneute Prüfung</p>
            <!-- ONE fixed-height row. The number input is ~42px tall and the checkbox ~20px, so without the
                 stated height the whole card jumped every time "Nie" was ticked (first manual smoke: "the Nie
                 checkbox jumps when the number input is shown, since it is way higher"). min-h-12 clears the
                 input at EVERY width (42px at sm+, 46px below it), so the row is the same height with or without
                 it and both controls share a baseline — with the Fristlänge row beside it, which states the same
                 height. -->
            <div class="flex min-h-12 flex-wrap items-center gap-x-3 gap-y-1"
                 role="group" [attr.aria-labelledby]="prefix + '-recheck-label'"
                 [attr.data-test]="prefix + '-recheck-row'">
              <cqme-checkbox [id]="prefix + '-recheck-never'" [attr.data-test]="prefix + '-recheck-never'" label="Nie"
                             [checked]="draft.recheckIntervalDays === null"
                             (checkedChange)="setRecheckNever(draft, $event)"></cqme-checkbox>
              @if (draft.recheckIntervalDays !== null) {
                <div class="flex items-center gap-2">
                  <input type="number" inputmode="numeric" step="1" [attr.min]="RecheckMin" [attr.max]="RecheckMax"
                         [id]="prefix + '-recheck-days'" [attr.data-test]="prefix + '-recheck-days'"
                         [ngModel]="draft.recheckIntervalDays" (ngModelChange)="draft.recheckIntervalDays = $event"
                         aria-label="Erneute Prüfung in Tagen"
                         [attr.aria-invalid]="recheckInvalid(draft)"
                         [attr.aria-describedby]="prefix + '-recheck-hint'"
                         class="block w-28 rounded-xl border-[1px] bg-white px-3 py-2.5 shadow-sm transition-all duration-200 focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"
                         [class.border-red-300]="recheckInvalid(draft)"
                         [class.border-gray-200]="!recheckInvalid(draft)"/>
                  <span class="text-sm text-gray-600">Tage</span>
                </div>
              }
            </div>
            <p class="mt-2 max-w-prose text-xs text-gray-600" [id]="prefix + '-recheck-hint'">{{ recheckHintFor(draft) }}</p>
            @if (recheckInvalid(draft)) {
              <p class="mt-2 text-xs font-semibold text-red-600"
                 [attr.data-test]="prefix + '-recheck-error'" role="alert">
                Bitte eine ganze Zahl zwischen {{ RecheckMin }} und {{ RecheckMax }} eintragen, oder „Nie" wählen.
              </p>
            }
          </div>
        </div>
      </div>
    </ng-template>

    <!-- Overview status pill + per-channel badges, shared by the table and card layouts. -->
    <ng-template #verdictBadge let-o>
      <span data-test="verdict-badge" [attr.data-verdict]="o.verdict"
            class="inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 align-top text-xs font-semibold"
            [class.bg-green-50]="o.verdict === Verdict.Ok" [class.text-green-700]="o.verdict === Verdict.Ok"
            [class.bg-amber-50]="o.verdict === Verdict.Review" [class.text-amber-700]="o.verdict === Verdict.Review"
            [class.bg-red-50]="o.verdict === Verdict.Restricted" [class.text-red-700]="o.verdict === Verdict.Restricted">
        {{ verdictLabel(o.verdict) }}
      </span>
    </ng-template>

    <ng-template #channelBadges let-o>
      @for (c of o.channels; track c.channel) {
        <!-- break-words, not whitespace-nowrap: "Telegram: Nicht verknüpft · Frist 01.10.2026" is wider than a
             narrow phone viewport, and a badge that cannot wrap is exactly how a card list starts scrolling
             sideways. -->
        <span data-test="channel-badge" [attr.data-linked]="c.linked" [title]="channelStatusHint(c)"
              class="inline-flex max-w-full items-center break-words rounded-full px-2 py-0.5 align-top text-xs font-semibold"
              [class.bg-green-50]="c.linked" [class.text-green-700]="c.linked"
              [class.bg-red-50]="!c.linked" [class.text-red-700]="!c.linked">
          {{ channelLabel(c.channel) }}: {{ channelBadgeText(c) }}
        </span>
      }
    </ng-template>
  `
})
export class ContactRequirementsComponent implements OnInit {
  protected readonly TargetType = ContactRequirementTargetType;
  protected readonly Policy = ContactRequirementPolicy;
  protected readonly Verdict = ContactGateVerdict;
  protected readonly GraceMin = GRACE_DAYS_MIN;
  protected readonly GraceMax = GRACE_DAYS_MAX;
  protected readonly RecheckMin = RECHECK_INTERVAL_MIN;
  protected readonly RecheckMax = RECHECK_INTERVAL_MAX;
  protected readonly DateFormat = DATE_FORMAT;

  protected readonly channelOptions = CONTACT_CHANNEL_OPTIONS;

  /**
   * The three target kinds, in display order, as the segmented strip renders them. A stable array so the strip
   * is a `@for` rather than three near-identical hand-written buttons — the `data-test` hooks are part of it so
   * they stay attached to their kind.
   */
  protected readonly targetKinds: ReadonlyArray<{value: ContactRequirementTargetType; label: string; test: string}> = [
    {value: ContactRequirementTargetType.AllEmployees, label: 'Alle Mitarbeiter', test: 'target-mode-all'},
    {value: ContactRequirementTargetType.EmploymentRelationship, label: 'Arbeitsverhältnis', test: 'target-mode-relationship'},
    {value: ContactRequirementTargetType.Employee, label: 'Mitarbeiter', test: 'target-mode-employee'}
  ];

  /** The two options every cohort dropdown offers, in display order — the default (`Immediate`) first. A single
   * stable array, never rebuilt per change-detection pass — see the identical note on the Forms requirements
   * screen's own `policyOptions`; `<cqme-select>` hands `[options]` straight to ng-select, which rebuilds its
   * item list whenever the array identity changes. */
  protected readonly policyOptions: PolicyOption[] = [
    {value: ContactRequirementPolicy.Immediate, label: contactRequirementPolicyLabel(ContactRequirementPolicy.Immediate)},
    {value: ContactRequirementPolicy.Grace, label: contactRequirementPolicyLabel(ContactRequirementPolicy.Grace)}
  ];

  protected loading = true;
  protected error = false;
  protected submitting = false;
  /** Public for templates + tests. */
  requirements: ContactRequirementResource[] = [];

  protected channel: ContactChannel = ContactChannel.Telegram;
  protected targetType: ContactRequirementTargetType = ContactRequirementTargetType.AllEmployees;
  /** The picked target's id, or `undefined` for "nothing picked yet"; public for templates + tests. */
  selectedTargetId: string | undefined = undefined;

  /** The gating settings the next assignment is created with — both cohorts `Immediate`, so a manager opts
   * *into* a window or a re-check, never out of one by accident. */
  newAssignment: ContactRequirementGatingDraft = defaultGatingDraft();

  /** The requirement currently open for in-place editing, or `null`. Public for templates + tests. */
  editingId: string | null = null;
  editDraft: ContactRequirementGatingDraft | null = null;

  /** Collapsed by default — see the template's note on why the overview loads lazily. */
  protected overviewOpen = false;
  protected overviewLoading = false;
  protected overviewError = false;
  protected overview: ContactRequirementOverviewEntry[] = [];

  private employees: Target[] = [];
  private relationships: Target[] = [];

  private readonly requirementHttp = inject(ContactRequirementHttpService);
  private readonly employeeHttp = inject(EmployeeHttpService);
  private readonly relationshipHttp = inject(EmploymentRelationshipHttpService);
  private readonly alert = inject(GlobalAlertService);
  private readonly dates = inject(DatePipe);
  private readonly zone = inject(NgZone);

  /** Resolved by template reference, not by type — see the identical note on the Forms requirements screen. */
  private readonly targetPicker = viewChild<SelectComponent<Target, string>>('targetPicker');

  // ----- the two floating-header tables ------------------------------------------------------------------------
  // Both lists are the employees roster's table, so both run the SHARED PinnedTableController rather than a
  // second copy of its measurement / scroll-sync / hand-over code. One instance each: they scroll independently,
  // and each clone is clamped to its own table's box (a sticky box cannot leave its containing block), so the
  // one at the pin line is always the table the reader is actually over.

  private readonly reqWrapper = viewChild<ElementRef<HTMLElement>>('reqWrapper');
  private readonly reqHead = viewChild<ElementRef<HTMLElement>>('reqHead');
  private readonly reqHeadRow = viewChild<ElementRef<HTMLElement>>('reqHeadRow');
  private readonly reqTable = viewChild<ElementRef<HTMLElement>>('reqTable');
  private readonly reqHeadViewport = viewChild<ElementRef<HTMLElement>>('reqHeadViewport');
  private readonly reqScrollViewport = viewChild<ElementRef<HTMLElement>>('reqScrollViewport');

  private readonly ovWrapper = viewChild<ElementRef<HTMLElement>>('ovWrapper');
  private readonly ovHead = viewChild<ElementRef<HTMLElement>>('ovHead');
  private readonly ovHeadRow = viewChild<ElementRef<HTMLElement>>('ovHeadRow');
  private readonly ovTable = viewChild<ElementRef<HTMLElement>>('ovTable');
  private readonly ovHeadViewport = viewChild<ElementRef<HTMLElement>>('ovHeadViewport');
  private readonly ovScrollViewport = viewChild<ElementRef<HTMLElement>>('ovScrollViewport');

  /** The assignments list's floating header, frozen column and pinned scrollbar. Public for templates + tests. */
  readonly requirementsTable = new PinnedTableController(this.zone, () => ({
    wrapper: this.reqWrapper()?.nativeElement,
    realHead: this.reqHead()?.nativeElement,
    realHeadRow: this.reqHeadRow()?.nativeElement,
    realTable: this.reqTable()?.nativeElement,
    headViewport: this.reqHeadViewport()?.nativeElement,
    scrollViewport: this.reqScrollViewport()?.nativeElement
  }));

  /** The roster overview's own instance of the same. Public for templates + tests. */
  readonly overviewTable = new PinnedTableController(this.zone, () => ({
    wrapper: this.ovWrapper()?.nativeElement,
    realHead: this.ovHead()?.nativeElement,
    realHeadRow: this.ovHeadRow()?.nativeElement,
    realTable: this.ovTable()?.nativeElement,
    headViewport: this.ovHeadViewport()?.nativeElement,
    scrollViewport: this.ovScrollViewport()?.nativeElement
  }));

  constructor() {
    // One effect per table. Each re-binds whenever its own parts appear or disappear — the assignments table
    // rides the loading / error / empty chain, the overview table the collapsed / loading / empty one — and
    // Angular runs the registered cleanup both on re-run and on destroy.
    effect(onCleanup => this.requirementsTable.bind(onCleanup));
    effect(onCleanup => this.overviewTable.bind(onCleanup));
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = false;

    forkJoin({
      requirements: this.requirementHttp.list(),
      // stats=false: the picker only maps id → "Vorname Nachname" (#457's convention, mirrored from Forms).
      employees: this.employeeHttp.listNl(undefined, false, false),
      relationships: this.relationshipHttp.List()
    }).subscribe({
      next: ({requirements, employees, relationships}) => {
        this.requirements = requirements;
        this.employees = employees.map(e => ({id: e.id, name: `${e.firstName} ${e.lastName}`.trim() || e.name}));
        this.relationships = relationships.map(r => ({id: r.id, name: r.name}));
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }

  // ----- the roster overview ---------------------------------------------------------------------------------

  toggleOverview(): void {
    this.overviewOpen = !this.overviewOpen;
    if (this.overviewOpen && this.overview.length === 0 && !this.overviewLoading) this.loadOverview();
  }

  loadOverview(): void {
    this.overviewLoading = true;
    this.overviewError = false;
    this.requirementHttp.overview().subscribe({
      next: entries => {
        this.overview = entries;
        this.overviewLoading = false;
      },
      error: () => {
        this.overviewError = true;
        this.overviewLoading = false;
      }
    });
  }

  verdictLabel(v: ContactGateVerdict): string {
    return contactGateVerdictLabel(v);
  }

  channelLabel(channel: ContactChannel): string {
    return contactChannelLabel(channel);
  }

  /**
   * The badge's own text. The last confirmation (or the open Frist) is IN the badge, not only in its tooltip:
   * a manager scanning the roster asks "is it linked, and how current is that" in one glance, and a hover-only
   * date answered the second half to nobody (first manual smoke: "last verified is not showing").
   */
  channelBadgeText(status: {linked: boolean; lastVerifiedUtc?: string | null; deadline?: string | null}): string {
    if (status.linked) {
      return status.lastVerifiedUtc
        ? `Verknüpft · bestätigt ${this.formatDate(status.lastVerifiedUtc)}`
        : 'Verknüpft';
    }
    return status.deadline
      ? `Nicht verknüpft · Frist ${this.formatDate(status.deadline)}`
      : 'Nicht verknüpft';
  }

  /** Tooltip for one overview row's per-channel badge — the badge's text as a full sentence. */
  channelStatusHint(status: {linked: boolean; lastVerifiedUtc?: string | null; deadline?: string | null}): string {
    if (status.linked) {
      return status.lastVerifiedUtc
        ? `Zuletzt bestätigt am ${this.formatDate(status.lastVerifiedUtc)}.`
        : 'Verknüpft.';
    }
    return status.deadline
      ? `Nicht verknüpft — Frist bis ${this.formatDate(status.deadline)}.`
      : 'Nicht verknüpft.';
  }

  /**
   * The one date formatter on this screen, for the three strings built in TypeScript rather than the template.
   *
   * Deliberately the Angular `DatePipe` with an explicit `dd.MM.yyyy` pattern, never `toLocaleDateString`: the
   * latter answers the browser's short form, which in `de-DE` is `9.9.2026` — unpadded, and different again in
   * every other locale, so half this screen's dates disagreed with the other half (first manual smoke). A
   * numeric pattern also makes the result locale-independent, which is what keeps it stable in tests.
   */
  private formatDate(value: string | null | undefined): string {
    return this.dates.transform(value, DATE_FORMAT) ?? '';
  }

  // ----- target picker ----------------------------------------------------------------------------------------

  setChannel(value: ContactChannel | ContactChannel[] | null | undefined): void {
    if (Array.isArray(value) || value == null) return;
    this.channel = value;
    this.clearPicker();
  }

  setTargetType(type: ContactRequirementTargetType): void {
    this.targetType = type;
    this.clearPicker();
  }

  /** Store the target the manager picked, or `undefined` when they cleared the control. Same normalisation as
   * the Forms requirements screen's `setSelectedTarget` — see its doc comment for why the parameter is wider
   * than what is stored. */
  setSelectedTarget(id: string | string[] | null | undefined): void {
    if (Array.isArray(id)) return;
    this.selectedTargetId = id ?? undefined;
  }

  /** Targets of the current type not already assigned this channel. Memoised on content for the same reason as
   * the Forms requirements screen's `availableTargets` — see its doc comment. */
  get availableTargets(): Target[] {
    if (this.targetType === ContactRequirementTargetType.AllEmployees) return [];
    const pool = this.targetType === ContactRequirementTargetType.Employee ? this.employees : this.relationships;
    const assigned = new Set(this.requirements
      .filter(r => r.channel === this.channel && r.targetType === this.targetType)
      .map(r => r.targetType === ContactRequirementTargetType.Employee ? r.employeeId : r.employmentRelationshipId));
    const key = `${this.channel}|${this.targetType}|${pool.map(t => `${t.id}:${t.name}`).join(',')}|${[...assigned].sort().join(',')}`;
    if (key !== this.availableTargetsKey) {
      this.availableTargetsKey = key;
      this.availableTargetsCache = pool.filter(t => !assigned.has(t.id));
    }
    return this.availableTargetsCache;
  }

  private availableTargetsCache: Target[] = [];
  private availableTargetsKey: string | null = null;

  private clearPicker(): void {
    this.selectedTargetId = undefined;
    this.targetPicker()?.reset();
  }

  // ----- gating settings ---------------------------------------------------------------------------------------

  setExistingPolicy(draft: ContactRequirementGatingDraft, value: ContactRequirementPolicy | ContactRequirementPolicy[] | null | undefined): void {
    const policy = this.singlePolicy(value);
    if (policy !== null) draft.existingStaffPolicy = policy;
  }

  setNewPolicy(draft: ContactRequirementGatingDraft, value: ContactRequirementPolicy | ContactRequirementPolicy[] | null | undefined): void {
    const policy = this.singlePolicy(value);
    if (policy !== null) draft.newStaffPolicy = policy;
  }

  /** Toggle the "Nie" checkbox: checked clears the interval to `null`, unchecked restores the default window. */
  setRecheckNever(draft: ContactRequirementGatingDraft, never: boolean): void {
    draft.recheckIntervalDays = never ? null : GRACE_DAYS_DEFAULT;
  }

  graceDaysInvalid(draft: ContactRequirementGatingDraft): boolean {
    return !isValidGraceDays(draft.graceDays);
  }

  recheckInvalid(draft: ContactRequirementGatingDraft): boolean {
    return !isValidRecheckIntervalDays(draft.recheckIntervalDays);
  }

  existingHintFor(draft: ContactRequirementGatingDraft): string {
    return existingStaffContactPolicyHint(draft.existingStaffPolicy, draft.graceDays ?? GRACE_DAYS_DEFAULT);
  }

  newHintFor(draft: ContactRequirementGatingDraft): string {
    return newStaffContactPolicyHint(draft.newStaffPolicy, draft.graceDays ?? GRACE_DAYS_DEFAULT);
  }

  recheckHintFor(draft: ContactRequirementGatingDraft): string {
    return recheckIntervalHint(draft.recheckIntervalDays);
  }

  /** The re-check column's value: the interval, or "Nie" when the requirement never re-checks. */
  recheckLabel(r: ContactRequirementResource): string {
    return r.recheckIntervalDays == null ? 'Nie' : `Alle ${r.recheckIntervalDays} Tage`;
  }

  get canSubmitNew(): boolean {
    if (this.submitting) return false;
    if (this.graceDaysInvalid(this.newAssignment) || this.recheckInvalid(this.newAssignment)) return false;
    if (this.targetType === ContactRequirementTargetType.AllEmployees) return true;
    return !!this.selectedTargetId;
  }

  get canSaveEdit(): boolean {
    return !!this.editDraft && !this.submitting
      && !this.graceDaysInvalid(this.editDraft) && !this.recheckInvalid(this.editDraft);
  }

  /**
   * A row's identity line. `targetName` is server-resolved and normally already carries "Ada Lovelace" /
   * "Vollzeit" / "Alle Mitarbeiter", but an `AllEmployees` requirement can come back with it EMPTY (observed on
   * a live shard: the row has no employee and no relationship to resolve a name from). A blank identity cell in
   * the frozen column reads as a broken row, so it falls back to the target kind — which is the whole of that
   * requirement's identity anyway.
   */
  targetName(r: ContactRequirementResource): string {
    return r.targetName?.trim() || this.targetTypeLabel(r);
  }

  /**
   * The secondary line under it: kind · channel. The kind is dropped when {@link targetName} has already fallen
   * back to it, so the row does not say "Alle Mitarbeiter" twice.
   */
  targetSubline(r: ContactRequirementResource): string {
    const channel = this.channelLabel(r.channel);
    return r.targetName?.trim() ? `${this.targetTypeLabel(r)} · ${channel}` : channel;
  }

  targetTypeLabel(r: ContactRequirementResource): string {
    switch (r.targetType) {
      case ContactRequirementTargetType.Employee:
        return 'Mitarbeiter';
      case ContactRequirementTargetType.EmploymentRelationship:
        return 'Arbeitsverhältnis';
      case ContactRequirementTargetType.AllEmployees:
        return 'Alle Mitarbeiter';
    }
  }

  existingHint(r: ContactRequirementResource): string {
    return existingStaffContactPolicyHint(r.existingStaffPolicy, r.graceDays);
  }

  newHint(r: ContactRequirementResource): string {
    return newStaffContactPolicyHint(r.newStaffPolicy, r.graceDays);
  }

  private singlePolicy(value: ContactRequirementPolicy | ContactRequirementPolicy[] | null | undefined): ContactRequirementPolicy | null {
    if (Array.isArray(value)) return null;
    return value ?? null;
  }

  // ----- create / edit / remove -------------------------------------------------------------------------------

  add(): void {
    if (!this.canSubmitNew) return;
    const draft = this.newAssignment;
    const graceDays = draft.graceDays ?? GRACE_DAYS_DEFAULT;
    this.submitting = true;
    this.requirementHttp.create({
      channel: this.channel,
      targetType: this.targetType,
      ...(this.targetType === ContactRequirementTargetType.Employee ? {employeeId: this.selectedTargetId} : {}),
      ...(this.targetType === ContactRequirementTargetType.EmploymentRelationship ? {employmentRelationshipId: this.selectedTargetId} : {}),
      mandatory: true,
      existingStaffPolicy: draft.existingStaffPolicy,
      newStaffPolicy: draft.newStaffPolicy,
      graceDays,
      recheckIntervalDays: draft.recheckIntervalDays
    }).subscribe({
      next: created => {
        this.requirements = [created, ...this.requirements];
        this.selectedTargetId = undefined;
        this.targetPicker()?.reset();
        this.newAssignment = defaultGatingDraft();
        this.submitting = false;
        this.alert.createAndShow('Zugewiesen', 'Die Pflicht-Kontakt-Zuweisung wurde gespeichert.', AlertLevel.success, 2000);
      },
      error: err => {
        this.submitting = false;
        this.alert.createAndShow('Fehler', this.describeError(err), AlertLevel.error, 3000);
      }
    });
  }

  toggleEdit(r: ContactRequirementResource): void {
    if (this.editingId === r.id) {
      this.cancelEdit();
      return;
    }
    this.editingId = r.id;
    this.editDraft = gatingDraftOf(r);
  }

  cancelEdit(): void {
    this.editingId = null;
    this.editDraft = null;
  }

  saveEdit(r: ContactRequirementResource): void {
    if (!this.canSaveEdit || !this.editDraft) return;
    const draft = this.editDraft;
    const graceDays = draft.graceDays ?? GRACE_DAYS_DEFAULT;
    this.submitting = true;
    this.requirementHttp.update(r.id, {
      mandatory: r.mandatory,
      existingStaffPolicy: draft.existingStaffPolicy,
      newStaffPolicy: draft.newStaffPolicy,
      graceDays,
      recheckIntervalDays: draft.recheckIntervalDays
    }).subscribe({
      next: updated => {
        this.requirements = this.requirements.map(x => x.id === r.id ? {...r, ...updated} : x);
        this.cancelEdit();
        this.submitting = false;
        this.alert.createAndShow('Gespeichert', 'Die Zuweisung wurde aktualisiert.', AlertLevel.success, 2000);
      },
      error: err => {
        this.submitting = false;
        this.alert.createAndShow('Fehler', this.describeError(err), AlertLevel.error, 3000);
      }
    });
  }

  remove(r: ContactRequirementResource): void {
    if (!confirm(`Zuweisung für "${this.targetName(r)}" entfernen?`)) return;
    this.requirementHttp.delete(r.id).subscribe({
      next: () => {
        this.requirements = this.requirements.filter(x => x.id !== r.id);
        if (this.editingId === r.id) this.cancelEdit();
        this.alert.createAndShow('Entfernt', 'Die Zuweisung wurde entfernt.', AlertLevel.success, 2000);
      },
      error: err => this.alert.createAndShow('Fehler', this.describeError(err), AlertLevel.error, 3000)
    });
  }

  /**
   * The German sentence for a failed call on this screen. Branches on HTTP status only, deliberately — never on
   * `error.description`, which the style guide's "Surfacing an API error" rule forbids rendering (it is the
   * `ErrorCatalog`'s English developer-facing summary). The exact `ContactLinkErrorCodes` this API answers with
   * were not yet available when this screen was built; add a `code`-keyed branch above the status fallback once
   * they land, the same way `ShiftMakerComponent.messageFor` does.
   */
  private describeError(err: unknown): string {
    const status = (err as {status?: unknown} | null)?.status;
    if (status === 403) return 'Keine Berechtigung für diese Aktion.';
    if (status === 400) return 'Diese Zuweisung wurde vom Server abgelehnt. Bitte prüfen Sie Ziel und Einstellungen.';
    return 'Die Aktion konnte nicht ausgeführt werden.';
  }
}
